import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, type AuthUser } from './api.js';

interface AuthState {
  loading: boolean;
  required: boolean;
  allowedDomain: string;
  user: AuthUser | undefined;
  refresh: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [loading, setLoading] = useState(true);
  const [required, setRequired] = useState(false);
  const [allowedDomain, setAllowedDomain] = useState('rocketphone.ai');
  const [user, setUser] = useState<AuthUser>();
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const config = await api.authConfig();
        if (cancelled) return;
        setRequired(config.googleConfigured);
        setAllowedDomain(config.allowedDomain);

        if (!config.googleConfigured) {
          setUser(undefined);
          return;
        }

        try {
          setUser(await api.me());
        } catch {
          if (!cancelled) setUser(undefined);
        }
      } catch {
        if (!cancelled) {
          setRequired(false);
          setUser(undefined);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  useEffect(() => {
    const onUnauthorized = (): void => {
      if (required) setUser(undefined);
    };
    window.addEventListener('sfpbx:unauthorized', onUnauthorized);
    return () => window.removeEventListener('sfpbx:unauthorized', onUnauthorized);
  }, [required]);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(undefined);
  }, []);

  const value = useMemo(
    () => ({ loading, required, allowedDomain, user, refresh, signOut }),
    [allowedDomain, loading, refresh, required, signOut, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
